/**
 * Authored seed codex (D7) — discovered ambiently, never a forced quest. The
 * story-director may grow these from world ground-truth later; these are the
 * hand-authored seeds the world starts with. Tasteful, personal-use homage.
 *
 * The triple contract holds here too: nothing is missable, time-bound, or
 * required. Every entry is *eventually reachable* by ordinary, unhurried play —
 * legends are flavor to stumble upon, not a checklist to complete.
 */
import type { CodexEntry } from '@/shared';
import { MEWTWO_CODEX_ENTRIES } from './mewtwoThread.js';

/**
 * The slice of player/world state codex unlocks read. Read-only; the lore layer
 * never mutates canon — it only observes it to decide what prose to reveal.
 */
export interface CodexWorldState {
  /** Overworld nodes the player has set foot on. */
  readonly visitedNodeIds: readonly string[];
  /** Species the player has recorded in their living dex. */
  readonly caughtSpeciesIds: readonly string[];
  /** How many rumors of each kind the town has murmured (e.g. 'bird-rumor'). */
  readonly rumorCounts: Readonly<Record<string, number>>;
  /** Total friendship across the simulated society (warmth accrued, never lost). */
  readonly friendshipTotal: number;
}

export const SEED_CODEX: readonly CodexEntry[] = [
  {
    id: 'mewtwo-origin',
    title: 'The One That Was Made',
    kind: 'legend',
    body:
      'They whisper of a Pokemon born not of egg nor evolution, but of ambition. In a laboratory ' +
      'far from any route a traveler walks, learned people bent over their lamps for years, coaxing ' +
      'a new life out of the faintest trace of an older legend — a single curl of genes, recovered ' +
      'from a place no map will name. They wanted to make something that had never been made. By ' +
      'every account, they succeeded, and by every account, they were not ready for what success ' +
      'felt like.\n\n' +
      'It woke stronger than its makers had dared to dream, and far lonelier. The story goes that ' +
      'it opened its eyes in a tank of pale light and understood, in a single cold instant, exactly ' +
      'what it was and exactly why it had been brought into the world: to be powerful, and to belong ' +
      'to someone. Neither of those, it decided, was the same as being loved. It does not seem to ' +
      'have forgiven that for a long, long while.\n\n' +
      'When it left — and the tales all agree that it left, and that the leaving was not gentle — it ' +
      'went looking. Not for a battle, the old breeders insist, though it could have flattened a ' +
      'mountain if it wished. It went looking for the simpler thing the laboratory never thought to ' +
      'give it: a reason for having been made at all that it could choose for itself.\n\n' +
      'Some say it still roams the high, quiet places where the wind keeps its secrets, watching the ' +
      'small kindnesses people show one another and the Pokemon at their side, weighing them. If you ' +
      'ever feel watched on a still route, with not a leaf moving and no trainer in sight, do not be ' +
      'afraid. It is only wondering — patiently, from very far away — whether you, too, are kind. ' +
      'The town believes that those who are kind have nothing at all to fear from the one that was ' +
      'made. The town has always rather hoped it might, one day, find its reason here.',
    unlock: { kind: 'always' },
    relatedSpeciesIds: ['mewtwo', 'mew'],
  },
  {
    id: 'mew-origin',
    title: 'The Little Dream at the Root of Everything',
    kind: 'legend',
    body:
      'Before the great families of Pokemon branched and bloomed, before there were types to chart ' +
      'or evolutions to wonder at, the old scholars say there was one small, pink, playful thing. It ' +
      'was light enough to ride a breeze and curious enough to follow one just to see where it went. ' +
      'Folded up inside it, they say, was every later shape the world would ever wear — every fin and ' +
      'feather and flame — held quietly, like a song that has been written but not yet sung.\n\n' +
      'That is the part the scholars love to argue over by lamplight. They claim that if you could ' +
      'somehow read the whole long story of every Pokemon there is, you would find the very first ' +
      'line of it written in this one tiny, drifting creature. The mighty legends of storm and tide, ' +
      'the gentle things that doze in the meadow grass, the friend curled at your own heel tonight — ' +
      'all of them, the tale insists, are leaves on a tree whose first small seed was this.\n\n' +
      'And yet for something so vast in what it holds, it asks for nothing. It is shy, and it is ' +
      'rarely seen, and that is exactly as it likes things. It plays where no one is looking. It ' +
      'turns invisible the way a child hides behind laughter — not to deceive anyone, only because ' +
      'hiding is half the fun. Those few who do glimpse it never speak afterward of catching it, or ' +
      'of how strong it might be. They speak only of how, for one breath, the whole world felt ' +
      'younger and gentler and more forgiving than they had ever let themselves believe.\n\n' +
      'The breeders tell the little ones that you cannot go hunting for the dream at the root of ' +
      'everything; you can only become the sort of person it might, one warm afternoon, decide to ' +
      'play near. That is no loss. It is simply the way of small, good things — they come to the ' +
      'quiet and the kind, in their own time, and never on demand.',
    unlock: { kind: 'friendship', ref: 'society', threshold: 200 },
    relatedSpeciesIds: ['mew'],
  },
  {
    id: 'legendary-birds',
    title: 'The Three Who Rule the Skies',
    kind: 'legend',
    body:
      'Old sailors mark three storms by name, the way they mark three friends, and they will tell ' +
      'you the weather has a temperament if only you learn to read it. One storm freezes the air to ' +
      'glittering glass and leaves frost ferns blooming on every windowpane; the world it passes over ' +
      'goes still and blue and perfectly silent, as though the sky were holding its breath. The ' +
      'sailors call that one the bird of winter, and they speak of it softly.\n\n' +
      'The second trails thunder in its wake, and where it banks and turns the clouds split open with ' +
      'light. It is not cruel, the old hands say — only enormous, and forgetful of how small the rest ' +
      'of us are beneath it. Fishermen have sworn they saw it ride the head of a gale for the sheer ' +
      'joy of the gale, lightning pouring off its wings like spray off a prow.\n\n' +
      'The third leaves spring wherever it lands. The tales claim that a single feather, drifting ' +
      'down, can coax green out of a burnt hillside and warmth back into the longest winter; that ' +
      'whole valleys have woken early from the cold simply because it chose to pass that way. Of the ' +
      'three it is seen least of all, and longed for most.\n\n' +
      'Few have truly seen them, and fewer still are believed when they say so — a glimpse of one is ' +
      'the kind of story a person carries quietly for the rest of their life. But every region keeps ' +
      'a shrine for them all the same, swept and tended, just in case the sky is listening. The ' +
      'keepers will tell you it costs nothing to leave a little kindness out for something so much ' +
      'larger than yourself, and that on the days the wind turns suddenly gentle, it is pleasant to ' +
      'imagine you know exactly who to thank.',
    unlock: { kind: 'rumorCount', ref: 'bird-rumor', threshold: 3 },
    relatedSpeciesIds: ['articuno', 'zapdos', 'moltres'],
  },
  {
    id: 'region-history',
    title: 'How the Routes Came to Run Parallel',
    kind: 'region',
    body:
      'Long before there were towns, before there was a single signpost or a swept doorstep, there ' +
      'were paths — and even the paths were not made on purpose. They were worn slowly into the land ' +
      'by Pokemon following the seasons: the herds that drifted toward the warm meadows in spring, ' +
      'the flocks that traced the coast on their way south, the small shy things that always seemed ' +
      'to know the safest way down a mountainside in the dark. The land simply remembered where so ' +
      'many gentle feet had passed, and kept the memory as a trail.\n\n' +
      'The first trainers did the wisest thing anyone has ever done in these parts: nothing clever ' +
      'at all. They simply walked beside the Pokemon, on the paths the Pokemon had already chosen, ' +
      'and let themselves be led to the good water and the sheltered hollows and the high places ' +
      'where the view made a long day feel worth it. They did not conquer the region. They were ' +
      'shown around it, like guests, and they had the grace to behave like guests.\n\n' +
      'So the region grew the way a friendship grows — one shared road at a time, no road more ' +
      'important than another. That is why the routes here run parallel rather than toward some ' +
      'single grand capital: there was never one place everyone was hurrying to. There were only ' +
      'many ways to wander, all of them worth wandering, all of them leading home again by evening.\n\n' +
      'It is also why, the old folk say, no two days on the routes are ever quite the same. The ' +
      'paths still belong, in their hearts, to the creatures that first wore them in; and creatures, ' +
      'unlike maps, are allowed to change their minds. Walk the same road twice and the weather, the ' +
      'company, the small wild faces peering from the grass will all have shifted a little. That is ' +
      'not the region being unreliable. That is the region being alive.',
    unlock: { kind: 'always' },
    relatedSpeciesIds: [],
  },
  {
    id: 'the-old-shrine',
    title: 'The Shrine Nobody Built',
    kind: 'region',
    body:
      'At the edge of the first town stands a little weathered shrine, no taller than a child, its ' +
      'wood gone silver with age and its single step worn into a soft hollow by who-knows-how-many ' +
      'years of sitting. And the funny thing — the thing the town tells every newcomer with a kind ' +
      'of fond pride — is that no one remembers raising it. There is no founder, no plaque, no story ' +
      'of who first set the posts. Each generation simply arrived, found it already standing, swept ' +
      'its step, and left a berry on the ledge, the way the generation before had done.\n\n' +
      'Ask the keepers who it is for and you will get a different answer every time, and all the ' +
      'answers will be right. It is for whatever the sky sends, the oldest of them say — bird, or ' +
      'wind, or only the quiet. It is for the travelers who never came back, say the ones with long ' +
      'memories, lighting it so the way home stays marked. It is for nobody and everybody, shrug the ' +
      'young ones, and they leave their berry anyway, because it would feel strange not to.\n\n' +
      'What the shrine is truly for, perhaps, is the sitting. Half the town has spent an hour on that ' +
      'worn step at one time or another — when a day ran long, or a worry would not lie down, or a ' +
      'goodbye needed somewhere soft to be said. People bring their newest catch there to introduce ' +
      'it to the view. People bring their oldest friend there to sit in comfortable silence. The ' +
      'shrine asks nothing and judges nothing; it only holds the spot.\n\n' +
      'So if the town ever feels too full and bright and chatty, follow the path to its quiet edge ' +
      'and sit a while on the shrine nobody built. Leave a berry if you like, or do not. The wind ' +
      'will find you there either way, and the town will have kept your spot warm for whenever you ' +
      'wander back.',
    unlock: { kind: 'visited', ref: 'town-shrine' },
    relatedSpeciesIds: ['articuno', 'zapdos', 'moltres'],
  },
  {
    id: 'the-lantern-keeper',
    title: 'The Lantern at the Crossroads',
    kind: 'tale',
    body:
      'There is a tale the breeders tell the way a lullaby is told, slowly and the same every time, ' +
      'because the comfort is in the sameness. Long ago, they say, a trainer walked home in the dark ' +
      'along a crossroads where the right turning is hard to find — and was joined, without a word ' +
      'asked, by a small glowing Pokemon who simply fell into step and lit the path ahead. The ' +
      'trainer never learned where it had been going before, or whether it had been going anywhere ' +
      'at all. It seemed only to have decided that no one should have to find the turning alone.\n\n' +
      'They never battled, the two of them. They never traded, never trained for anything, never ' +
      'tested who was stronger, because the question never once came up between them. The little ' +
      'light met the trainer at the same crossroads the next dark evening, and the next, and the ' +
      'one after that, and walked the long way home each time with its quiet glow held steady — and ' +
      'somewhere in all those ordinary evenings the walking-together stopped being a favor and ' +
      'became simply what they were to each other.\n\n' +
      'Year on year it went, through the trainer’s busy seasons and slow ones, through good harvests ' +
      'and hard winters, the lantern at the crossroads as faithful as a heartbeat. And when at last ' +
      'the trainer grew old and the walks grew short, the small light shortened its walks to match, ' +
      'and never once seemed to mind that the road they shared had gotten so gentle.\n\n' +
      'The moral, the breeders say — and here their voices go soft, because this is the part they ' +
      'are really telling you — is that the best company keeps no score. It does not measure what ' +
      'you can give it or whether you are winning. It simply walks beside you and holds up a little ' +
      'light. That is why, in this region, you are never really walking a route alone: somewhere ' +
      'along it, the tale promises, there is always something glad to fall into step with you, and ' +
      'ask for nothing but the company.',
    unlock: { kind: 'always' },
    relatedSpeciesIds: [],
  },
  {
    id: 'first-friend',
    title: 'Your First Friend',
    kind: 'tale',
    body:
      'Every chronicle in the region opens the same way, and the keepers would not have it any other ' +
      'way: with the very first Pokemon a trainer chose to walk beside. Not the strongest they would ' +
      'ever raise — that one usually comes later, after many roads. Not the rarest, nor the one whose ' +
      'name makes the chat go quiet with admiration. Simply the first to look back at them across the ' +
      'grass and, for reasons entirely its own, decide to stay.\n\n' +
      'The town keeps a particularly soft spot for these starting pairs. The regulars will ask after ' +
      'your first friend by name for years — long after it has evolved, long after it has retired to ' +
      'doze in the day-camp sun while younger teammates take the routes. They remember the morning ' +
      'you arrived together when you have half forgotten it yourself. Around here, a trainer and ' +
      'their first companion are spoken of almost as one thing, the way you might speak of two old ' +
      'friends who finish each other’s sentences.\n\n' +
      'It is worth knowing, on the days the routes feel quiet or a catch slips away, that this first ' +
      'bond was never about power. It was the moment you learned the whole gentle shape of the thing ' +
      'you are doing out here: choosing someone, and being chosen back, and then simply going on ' +
      'together to see what the day holds. Every friend you make after this one is only the world ' +
      'saying yes again to a question your first friend already answered.\n\n' +
      'So this page is left open, and it begins wherever you do. Whoever looked back at you first — ' +
      'their name belongs at the top of it. The rest, the town is certain, will be a long and lovely ' +
      'story, and there is all the time in the world to see how it unfolds.',
    unlock: { kind: 'always' },
    relatedSpeciesIds: [],
  },
];

/** Does the player's world satisfy this entry's (ambient) unlock condition? */
export function isUnlocked(entry: CodexEntry, world: CodexWorldState): boolean {
  const u = entry.unlock;
  switch (u.kind) {
    case 'always':
      return true;
    case 'visited':
      return u.ref !== undefined && world.visitedNodeIds.includes(u.ref);
    case 'caught':
      return u.ref !== undefined && world.caughtSpeciesIds.includes(u.ref);
    case 'rumorCount':
      return (
        u.ref !== undefined &&
        u.threshold !== undefined &&
        (world.rumorCounts[u.ref] ?? 0) >= u.threshold
      );
    case 'friendship':
      return u.threshold !== undefined && world.friendshipTotal >= u.threshold;
    default:
      // Unknown / future condition: fail closed (stay locked), never throw.
      return false;
  }
}

/** All seed entries currently revealed for this world (monotonic as the world fills). */
export function unlockedEntries(world: CodexWorldState): readonly CodexEntry[] {
  return SEED_CODEX.filter((e) => isUnlocked(e, world));
}

/**
 * Every AUTHORED codex entry — the ambient seeds plus the Mewtwo thread's reward-gated
 * story pages. The reward-gated entries have `unlock.kind === 'badges'`, which
 * {@link isUnlocked} fails closed on, so they never appear ambiently via
 * {@link unlockedEntries}; they are revealed only when a quest's `codex-unlock` reward
 * pushes them into `LoreState.entries` (Agent B's grantReward). This registry is the
 * lookup that grant uses to resolve an entry id → its authored prose.
 */
export const ALL_CODEX: readonly CodexEntry[] = [...SEED_CODEX, ...MEWTWO_CODEX_ENTRIES];

/** Resolve an authored codex entry by id (ambient seed OR reward-granted story page). */
export function codexEntryById(id: string): CodexEntry | undefined {
  return ALL_CODEX.find((e) => e.id === id);
}
